import { FC, useEffect, useState } from "react";
import { Snackbar, Alert, Box, Typography } from "@mui/material";
import type { UserClassAchievement } from "../../api-client/models/userClassAchievement";
import { myMutate } from "../../hooks/useUser";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useNavigate } from "react-router-dom";
import {
  useUserClassAchievementsList,
  userClassAchievementsMarkReadCreate,
  getUserClassAchievementsListQueryKey,
} from "../../api-client/user-class-achievements/user-class-achievements";
import { STALE_TIME } from "../../utils/constants.ts";

const AchievementPopup: FC = () => {
  const navigate = useNavigate();
  const [currentAchievement, setCurrentAchievement] =
    useState<UserClassAchievement | null>(null);
  const [queue, setQueue] = useState<UserClassAchievement[]>([]);

  const { data: achievements } = useUserClassAchievementsList({
    query: {
      staleTime: STALE_TIME, // 30 seconds
      refetchOnWindowFocus: true,
    },
  });

  // queue is a drain-over-time state machine fed by achievements, which
  // refetches on a timer independent of render (an external system) - not a
  // simple derived value, so an effect is the right tool here.
  useEffect(() => {
    if (achievements) {
      const unreadAchievements = achievements.filter((a) => !a.isRead);
      if (unreadAchievements.length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setQueue(unreadAchievements);
      }
    }
  }, [achievements]);

  // Pops the next queued achievement once the current popup is dismissed.
  useEffect(() => {
    if (queue.length > 0 && !currentAchievement) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentAchievement(queue[0]);
      setQueue((prev) => prev.slice(1));
    }
  }, [queue, currentAchievement]);

  const handleClose = () => {
    if (currentAchievement) {
      userClassAchievementsMarkReadCreate(currentAchievement.id, {
          isRead: true,
          userClass: 0, // hack
          achievement: 0,
        })
        .then(() => {
          myMutate(getUserClassAchievementsListQueryKey()[0]);
        });
      setCurrentAchievement(null);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Don't navigate if clicking the close button
    const target = event.target as EventTarget | null;
    if (target && target instanceof Element && target.closest(".MuiAlert-action")) {
      return;
    }
    handleClose();
    navigate("/app/achievements");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick(event as unknown as React.MouseEvent<HTMLElement>);
    }
  };

  return (currentAchievement && (<Snackbar
    open={true}
    autoHideDuration={6000}
    onClose={handleClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
    sx={{ mt: 8 }}
  >
    <Alert
      onClose={handleClose}
      severity="success"
      variant="filled"
      icon={<EmojiEventsIcon />}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label="View achievement details"
      sx={{
        minWidth: 300,
        cursor: "pointer",
        "& .MuiAlert-icon": {
          fontSize: 40,
        },
      }}
    >
      <Box>
        <Typography variant="body1" sx={{
          fontWeight: "bold"
        }}>
          {currentAchievement?.achievementName}
        </Typography>
        <Typography variant="body2">
          {currentAchievement?.achievementDescription}
        </Typography>
      </Box>
    </Alert>
  </Snackbar>));
};

export default AchievementPopup;
